import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Phone, Mail, MapPin, Send, CheckCircle2, Loader2, AlertCircle } from 'lucide-react'
import { PortalLayout } from '../../components/portal/PortalLayout'
import { Seo } from '../../components/portal/Seo'
import { Container, Section, SectionHeading, Breadcrumbs, Card, Button, Alert, FormField } from '../../components/portal/ui'
import { submitContact, PURPOSE_LABELS, type ContactPurpose } from '../../services/contactService'

const PURPOSES = Object.keys(PURPOSE_LABELS) as ContactPurpose[]

export default function ContactPage() {
  const { t } = useTranslation()
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    purpose: PURPOSES[0] as ContactPurpose,
    message: '',
  })

  const validate = () => {
    const e: Record<string, string> = {}
    if (!form.name.trim()) e.name = t('pages.contact.form.errName')
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t('pages.contact.form.errEmail')
    if (form.message.trim().length < 10) e.message = t('pages.contact.form.errMessage')
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const onSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault()
    setServerError(null)
    if (!validate()) return
    setSubmitting(true)
    try {
      await submitContact({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        purpose: form.purpose,
        message: form.message.trim(),
      })
      setSubmitted(true)
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        t('pages.contact.form.errServer')
      setServerError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const purposeLabel = (p: ContactPurpose) =>
    t(`pages.contact.purposes.${p}`, { defaultValue: PURPOSE_LABELS[p] })

  return (
    <PortalLayout>
      <Seo
        title={t('pages.contact.seo.title')}
        description={t('pages.contact.seo.description')}
        canonicalPath="/contact"
        breadcrumbs={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.contact', 'Contact'), path: '/contact' }]}
      />
      <Section className="bg-gradient-mesh pb-6">
        <Container>
          <Breadcrumbs items={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.contact', 'Contact') }]} />
          <SectionHeading as="h1" eyebrow={t('pages.contact.hero.eyebrow')} title={t('pages.contact.hero.title')} description={t('pages.contact.hero.description')} />
        </Container>
      </Section>

      <Section className="bg-white pt-4">
        <Container>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Quick contacts */}
            <div className="space-y-4">
              <Card className="p-5 flex items-start gap-3">
                <Phone className="w-5 h-5 text-[rgb(15_76_129)] shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="font-semibold text-snow-900">{t('pages.contact.quickContacts.helplineTitle')}</p>
                  <a href="tel:1800116666" className="text-sm text-[rgb(15_76_129)] hover:underline">{t('pages.contact.quickContacts.helplineNumber')}</a>
                  <p className="text-xs text-snow-500 mt-0.5">{t('pages.contact.quickContacts.helplineNote')}</p>
                </div>
              </Card>
              <Card className="p-5 flex items-start gap-3">
                <Mail className="w-5 h-5 text-[rgb(15_76_129)] shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="font-semibold text-snow-900">{t('pages.contact.quickContacts.emailTitle')}</p>
                  <p className="text-sm text-snow-600">{t('pages.contact.quickContacts.emailValue')}</p>
                </div>
              </Card>
              <Card className="p-5 flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[rgb(15_76_129)] shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="font-semibold text-snow-900">{t('pages.contact.quickContacts.addressTitle')}</p>
                  <p className="text-sm text-snow-600">{t('pages.contact.quickContacts.addressValue')}</p>
                  <p className="text-xs text-snow-500">{t('pages.contact.quickContacts.addressNote')}</p>
                </div>
              </Card>
              <Alert tone="warning" title={t('pages.contact.quickContacts.emergencyTitle')}>
                {t('pages.contact.quickContacts.emergencyBody')}
              </Alert>
            </div>

            {/* Form */}
            <Card className="p-6 lg:col-span-2">
              {submitted ? (
                <div className="text-center py-10">
                  <CheckCircle2 className="w-12 h-12 text-[rgb(0_168_107)] mx-auto mb-4" aria-hidden="true" />
                  <h2 className="text-xl font-bold text-snow-900">{t('pages.contact.form.successTitle')}</h2>
                  <p className="text-snow-600 mt-2 max-w-md mx-auto">
                    {t('pages.contact.form.successBody')}
                  </p>
                  <Button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', purpose: PURPOSES[0], message: '' }) }} variant="outline" className="mt-5">
                    {t('pages.contact.form.sendAnother')}
                  </Button>
                </div>
              ) : (
                <form onSubmit={onSubmit} noValidate>
                  <h2 className="text-lg font-bold text-snow-900 mb-4">{t('pages.contact.form.title')}</h2>

                  {serverError && (
                    <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-red-50 text-red-800 text-sm" role="alert">
                      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
                      <span>{serverError}</span>
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-4">
                    <FormField label={t('pages.contact.form.nameLabel')} htmlFor="c-name" required error={errors.name}>
                      <input
                        id="c-name" type="text" required value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className={`input ${errors.name ? 'input-error' : ''}`}
                        aria-invalid={!!errors.name} aria-describedby={errors.name ? 'c-name-error' : undefined}
                      />
                    </FormField>
                    <FormField label={t('pages.contact.form.emailLabel')} htmlFor="c-email" required error={errors.email}>
                      <input
                        id="c-email" type="email" required value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className={`input ${errors.email ? 'input-error' : ''}`}
                        aria-invalid={!!errors.email} aria-describedby={errors.email ? 'c-email-error' : undefined}
                      />
                    </FormField>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4 mt-4">
                    <FormField label={t('pages.contact.form.phoneLabel')} htmlFor="c-phone">
                      <input
                        id="c-phone" type="tel" value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="input"
                        autoComplete="tel"
                      />
                    </FormField>
                    <FormField label={t('pages.contact.form.purposeLabel')} htmlFor="c-purpose">
                      <select id="c-purpose" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value as ContactPurpose })} className="select">
                        {PURPOSES.map((p) => <option key={p} value={p}>{purposeLabel(p)}</option>)}
                      </select>
                    </FormField>
                  </div>

                  <div className="mt-4">
                    <FormField label={t('pages.contact.form.messageLabel')} htmlFor="c-message" required error={errors.message} helpText={t('pages.contact.form.messageHelp')}>
                      <textarea
                        id="c-message" required rows={5} value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        className={`input ${errors.message ? 'input-error' : ''}`}
                        aria-invalid={!!errors.message} aria-describedby={errors.message ? 'c-message-error' : undefined}
                      />
                    </FormField>
                  </div>
                  <div className="mt-5 flex items-center gap-3">
                    <Button type="submit" variant="primary" disabled={submitting} icon={submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}>
                      {submitting ? t('pages.contact.form.sending') : t('pages.contact.form.submit')}
                    </Button>
                    <p className="text-xs text-snow-500">{t('pages.contact.form.privacyNote')}</p>
                  </div>
                </form>
              )}
            </Card>
          </div>
        </Container>
      </Section>
    </PortalLayout>
  )
}